import { appContent } from "./content";
import type { CurrentUser, RecentQuery, WorkspaceView } from "./types";
import { escapeHtml, initialsFor } from "./utils";

export function appShellMarkup(user: CurrentUser | null, content: string, shellVariant: "default" | "login" = "default", settingsOpen = false): string {
  const isWorkspace = Boolean(user && !user.mustChangePassword);
  const isLogin = shellVariant === "login";
  return `
    <header class="topbar ${isWorkspace ? "workspace-topbar" : ""} ${isLogin ? "login-topbar" : ""}">
      <div class="topbar-brand-group">
        <div class="brand-wordmark ${isWorkspace || isLogin ? "workspace-wordmark" : ""}" aria-label="Ask Jim">
          <span class="brand-wordmark__text" data-text="Ask Jim">Ask Jim</span>
        </div>
        <div class="topbar-title-stack">
          <div class="topbar-kicker">${isWorkspace || isLogin ? appContent.topbar.compactKicker : appContent.topbar.standardKicker}</div>
          ${isWorkspace || isLogin ? `<div class="topbar-subtitle">${appContent.topbar.subtitle}</div>` : ""}
        </div>
      </div>
      ${user ? `
        <div class="account">
          <span class="account-avatar">${escapeHtml(initialsFor(user.username))}</span>
          <span>${escapeHtml(user.username)}</span>
          <span class="role">${escapeHtml(user.role.replace("_", " "))}</span>
          <button id="settingsButton" class="settings-button ${settingsOpen ? "active" : ""}" type="button" aria-label="Open settings" aria-pressed="${String(settingsOpen)}">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M10.9 2.5h2.2l.6 2.2c.6.2 1.1.5 1.6.9l2.1-.7 1.1 1.9-1.5 1.6c.1.6.1 1.2 0 1.8l1.5 1.6-1.1 1.9-2.1-.7c-.5.4-1 .7-1.6.9l-.6 2.2h-2.2l-.6-2.2c-.6-.2-1.1-.5-1.6-.9l-2.1.7-1.1-1.9 1.5-1.6a7 7 0 0 1 0-1.8L6.5 6.8l1.1-1.9 2.1.7c.5-.4 1-.7 1.6-.9l.6-2.2ZM12 8.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5Z" /></svg>
          </button>
          <button id="logoutButton" class="ghost">Sign out</button>
        </div>
      ` : isLogin ? `<div class="login-access-badge" aria-hidden="true">♙</div>` : ""}
    </header>
    <main class="${isWorkspace ? "workspace-main" : ""} ${isLogin ? "login-main" : ""}">${content}</main>
  `;
}

export function workspaceShellMarkup(activeView: WorkspaceView, content: string, user: CurrentUser | null, savedAnswersCount: number, sidebarCollapsed: boolean, recentQueries: RecentQuery[]): string {
  return `
    <section class="workspace-shell">
      <aside class="workspace-sidebar ${sidebarCollapsed ? "collapsed" : ""}" aria-label="Workspace navigation">
        <button id="sidebarCollapse" class="sidebar-collapse" type="button" aria-label="${sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}" aria-pressed="${String(sidebarCollapsed)}">${sidebarCollapsed ? "»" : "«"}</button>
        <div class="sidebar-section">
          <div class="sidebar-label">${appContent.sidebar.sectionLabel}</div>
          <h2>${appContent.sidebar.heading}</h2>
          <button id="sidebarSearch" class="sidebar-item ${activeView === "search" ? "active" : ""}" type="button" title="${appContent.sidebar.ask}">
            <span class="sidebar-icon" aria-hidden="true">⌕</span><span class="sidebar-copy"><span>${appContent.sidebar.ask}</span><small>${appContent.sidebar.askSubtitle}</small></span>
          </button>
          <button id="sidebarLibrary" class="sidebar-item ${activeView === "library" ? "active" : ""}" type="button" title="${appContent.sidebar.library}">
            <span class="sidebar-icon" aria-hidden="true">☰</span><span class="sidebar-copy"><span>${appContent.sidebar.library}</span><small>${appContent.sidebar.librarySubtitle}</small></span>
          </button>
          <button id="sidebarSaved" class="sidebar-item ${activeView === "saved" ? "active" : ""}" type="button" title="${appContent.sidebar.saved}">
            <span class="sidebar-icon" aria-hidden="true">‡</span><span class="sidebar-copy"><span>${appContent.sidebar.saved}</span><small>${savedAnswersCount ? `${savedAnswersCount} ${savedAnswersCount === 1 ? appContent.sidebar.savedCountSingular : appContent.sidebar.savedCountPlural}` : appContent.sidebar.savedSubtitle}</small></span>
          </button>
          <button id="sidebarSearchLegacy" class="visually-hidden" type="button"><span>${appContent.sidebar.legacySearch}</span></button>
        </div>
        <section class="sidebar-recent-queries" aria-labelledby="sidebarRecentQueriesLabel">
          <div class="sidebar-recent-divider"></div>
          <div id="sidebarRecentQueriesLabel" class="sidebar-label">${appContent.sidebar.recentQueriesLabel}</div>
          ${recentQueries.length ? recentQueries.slice(0, 3).map((item) => `
            <button class="recent-query" type="button" data-recent-query="${escapeHtml(item.query)}">
              <strong>${escapeHtml(item.query)}</strong>
              ${item.terms.length ? `<small>${escapeHtml(item.terms.join(" · "))}</small>` : ""}
            </button>
          `).join("") : `<p class="sidebar-recent-empty">${appContent.sidebar.recentQueriesEmpty}</p>`}
        </section>
      </aside>
      <div class="workspace-content">${content}</div>
    </section>
  `;
}

export function mobileWorkspaceNav(activeView: WorkspaceView): string {
  return `
    <nav class="mobile-bottom-nav" aria-label="Mobile workspace navigation">
      <button id="mobileNavAsk" class="${activeView === "search" ? "active" : ""}" type="button" aria-label="Ask">＋</button>
      <button id="mobileNavLibrary" class="${activeView === "library" ? "active" : ""}" type="button" aria-label="Library">▤</button>
      <button id="mobileNavSaved" class="${activeView === "saved" ? "active" : ""}" type="button" aria-label="Saved">‡</button>
    </nav>
  `;
}
